/**
 * When `NEXT_PUBLIC_API_BASE_URL` is `/api/proxy`, strip client Authorization
 * headers and send session cookies so the proxy attaches the token server-side
 * (avoids HTTP 431 — Request Header Fields Too Large).
 */
import axios, { AxiosHeaders, type AxiosInstance } from "axios";
import { isBrowserApiProxied } from "@/lib/browserBackend";

function stripAuthorizationHeader(
  headers: AxiosHeaders | Record<string, unknown>
): void {
  if (headers instanceof AxiosHeaders) {
    headers.delete("Authorization");
    headers.delete("authorization");
    return;
  }
  delete (headers as Record<string, unknown>).Authorization;
  delete (headers as Record<string, unknown>).authorization;
}

function attachProxySafeAuth(instance: AxiosInstance): void {
  instance.interceptors.request.use((config) => {
    if (!isBrowserApiProxied()) {
      return config;
    }
    config.withCredentials = true;
    if (config.headers) {
      const headers = AxiosHeaders.from(config.headers);
      stripAuthorizationHeader(headers);
      config.headers = headers;
    }
    return config;
  });
}

if (typeof window !== "undefined" && isBrowserApiProxied()) {
  attachProxySafeAuth(axios);

  const originalCreate = axios.create.bind(axios);
  axios.create = ((...args: Parameters<typeof axios.create>) => {
    const instance = originalCreate(...args);
    attachProxySafeAuth(instance);
    return instance;
  }) as typeof axios.create;

  const originalFetch = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    const nextInit: RequestInit = {
      ...init,
      credentials: init?.credentials ?? "include",
    };
    if (init?.headers) {
      const headers = new Headers(init.headers);
      headers.delete("Authorization");
      headers.delete("authorization");
      nextInit.headers = headers;
    }
    return originalFetch(input, nextInit);
  };

  const originalSetRequestHeader = XMLHttpRequest.prototype.setRequestHeader;
  XMLHttpRequest.prototype.setRequestHeader = function (
    name: string,
    value: string
  ) {
    if (name.toLowerCase() === "authorization") {
      return;
    }
    return originalSetRequestHeader.call(this, name, value);
  };
}
